import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Stack, Text, Center, Box, Modal } from '@mantine/core';
import { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AssistantMessage } from '../AssistantMessage';
import { UserMessage } from '../UserMessage';
import { MessageAttachmentList } from '../MessageAttachmentList';
import { LoadingState } from '../../../../components/common/LoadingState';
import { ErrorState } from '../../../../components/common/ErrorState';
import { DynamicConfirmationModal } from '../../../../components/layout/AppShell/components/DynamicConfirmationModal';
import type { ConversationMessage, MessageActionType } from '../../types/conversation';
import type { ConversationDetailResponse } from '../../../../api/conversations';
import type { DigitalMaintenanceStatus } from '../../../../api/conversations';
import { transformConversationHistory } from '../../../../api/conversations';
import { useMessagesWithStore } from '../../../../hooks/useMessages';
import { useEditMessage } from '../../../../hooks/useEditMessage';
import { useChatStore } from '../../../../stores/chatStore';
import { useStreamingStore } from '../../../../stores/streamingStore';
import { useIsMessageSending } from '../../../../hooks/useMessageSendingState';
import { useChatActions, isChatSendInFlight } from '../../../../hooks/useSendMessage';
import { conversationQueryKeys } from '../../../../api/conversations';
import { FEATURES } from '../../../../config/featureFlags';
import {
  DIGITAL_MAINTENANCE_RESULT_COMMAND,
  canShowDigitalMaintenanceCheckResult,
  isDigitalMaintenanceOnly,
  isDigitalMaintenancePendingResult,
} from '../../utils/digitalMaintenanceCheckResult';
import { showNotification } from '../../../../components/Notification';

import classes from './ConversationContent.module.css';

interface AgentInfo {
  id: string;
  name: string;
  type: "generic" | "genie" | "ppl_document" | "safecoop_file" | "websearch";
}

interface Message {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  digital_maintenance_status?: DigitalMaintenanceStatus;
  timestamp?: Date;
  metadata?: {
    agentInfo?: AgentInfo[];
    [key: string]: unknown;
  };
}

interface ConversationContentProps {
  conversationId: string;
  messages?: Message[];
  loading?: boolean;
  error?: string;
  query?: UseQueryResult<ConversationDetailResponse>;
  isHomeScreen?: boolean;
  viewportRef?: React.RefObject<HTMLDivElement>;
}



interface SampleMessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
}

const SampleMessageBubble = ({ role, content }: SampleMessageBubbleProps) => {
  // Create sample message for our enhanced components
  const sampleMessage: ConversationMessage = {
    id: `sample-${role}`,
    role: role,
    content: content,
    timestamp: new Date(),
    actions: {
      canEdit: role === 'user',
      canDelete: true,
      canRegenerate: role === 'assistant',
      canPlayAudio: role === 'assistant',
      isBookmarked: false,
      isLiked: false,
      isDisliked: false,
    },
    metadata: {
      agentInfo: role === 'assistant' ? [{
          id: 'knowledge-agent',
          name: 'Knowledge Agent',
          type: 'generic' as const,
        }] : undefined,
    },
    // No thought process data - let the component use mock data
  };

  const handleSampleAction = () => {
    // No actions for sample messages
  };

  if (role === 'user') {
    return (
      <UserMessage
        message={sampleMessage}
        onAction={handleSampleAction}
        showActions={true}
      />
    );
  }

  return (
    <AssistantMessage
      message={sampleMessage}
      onAction={handleSampleAction}
      showActions={true}
    />
  );
};

// Memoized message component for performance
const MemoizedMessageComponent = React.memo(({ message, conversationId, onAction, showCheckResult, onCheckResult, isCheckingResult }: {
  message: ConversationMessage;
  conversationId: string;
  onAction: (action: MessageActionType, messageId: string) => void;
  showCheckResult?: boolean;
  onCheckResult?: () => void;
  isCheckingResult?: boolean;
}) => {
  if (message.role === 'user') {

    return (
      <UserMessage
        message={message}
        conversationId={conversationId}
        onAction={onAction}
        showActions={true}
      />
    );
  }

  if (message.role === 'assistant') {
    return (
      <AssistantMessage
        message={message}
        conversationId={conversationId}
        onAction={onAction}
        showActions={true}
        showCheckResult={showCheckResult}
        onCheckResult={onCheckResult}
        isCheckingResult={isCheckingResult}
      />
    );
  }

  return null;
});

MemoizedMessageComponent.displayName = 'MemoizedMessageComponent';

// Shared layout constants for consistent positioning
const CONVERSATION_LAYOUT = {
  MAX_WIDTH: 918,
  GAP: 'md' as const,
} as const;

const ConversationLoadingState = ({ isTransitioning = false }: { isTransitioning?: boolean }) => (
  <LoadingState
    message={isTransitioning ? "Switching conversation..." : "Loading conversation..."}
    showSkeleton={true}
    skeletonType="conversation"
    overlay={isTransitioning}
  />
);

const ConversationErrorState = ({ error, onRetry }: { error: string; onRetry?: () => void }) => (
  <ErrorState
    title="Failed to load conversation"
    message={error}
    onRetry={onRetry}
    retryLabel="Reload conversation"
  />
);

const ConversationEmptyState = () => (
  <Center style={{ flex: 1, padding: '2rem' }}>
    <Stack align="center" gap="md">
      <Text size="xl" fw={500} c="dimmed">
        Continue your conversation
      </Text>
      <Text size="sm" c="dimmed">
        Pick up where you left off or start fresh
      </Text>

      {/* Sample chat bubbles from existing design */}
      <Stack gap={CONVERSATION_LAYOUT.GAP} mt="xl" maw={CONVERSATION_LAYOUT.MAX_WIDTH} w="100%">
        <SampleMessageBubble
          role="user"
          content="What are our AI standards and compliance requirements today?"
        />
        <SampleMessageBubble
          role="assistant"
          content="I'll help you understand Fonterra's AI standards and compliance requirements. Here are the key areas we need to cover..."
        />
      </Stack>
    </Stack>
  </Center>
);

export const ConversationContent = React.memo(({
  conversationId,
  messages,
  loading = false,
  error,
  query,
  isHomeScreen = false,
  viewportRef
}: ConversationContentProps) => {

  const latestMessageID = messages && messages.length > 0 ? messages[messages.length - 1].id || '' : '';
  const { setMessageID, setConversationID, openPanel } = useMessagesWithStore({ conversationID: '', messageID: '' });

  const handleShowThoughtProcess = useCallback((messageID: string) => {
    openPanel();
    setConversationID(conversationId);
    setMessageID(messageID.replace(/-assistant$/, ''));
  }, [conversationId, setMessageID, setConversationID, openPanel]);

  const { editMessage } = useEditMessage(conversationId);

  // Get editing state management from chat store
  const { setEditingMessage, clearEditingMessage } = useChatStore();

  // Immediate scroll function for message sending (no delays)
  const scrollToBottomInstant = useCallback(() => {
    if (!viewportRef?.current) return;

    const viewport = viewportRef.current;
    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior: 'smooth',
    });
  }, [viewportRef]);

  // Robust scroll function for conversation switching (handles content rendering)
  const scrollToBottomRobust = useCallback((context = 'unknown') => {

    if (!viewportRef?.current) {
      return;
    }

    const viewport = viewportRef.current;

    const performScroll = () => {

      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: 'smooth',
      });

      // Verify and retry if needed for conversation switching
      setTimeout(() => {
        const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;

        if (distanceFromBottom > 10) {
          viewport.scrollTo({
            top: viewport.scrollHeight,
            behavior: 'instant',
          });
        }
      }, 300);
    };

    // Wait for content to render for conversation switching
    requestAnimationFrame(() => {
      requestAnimationFrame(performScroll);
    });
  }, [viewportRef]);

  // Access pending files for optimistic display on new messages
  const { getPendingFilesForMessage } = useChatStore();
  const check = useChatStore(state => state.digitalMaintenanceChecks[conversationId]);
  const buttonMessageId = check?.buttonMessageId;

  // Helper function to get files for a message (with smart optimistic display)
  const getMessageFiles = useCallback((message: Message, isUserMessage: boolean, messageIndex: number, allMessages: Message[]): string[] => {
    const serverFiles = (message as any).uploadedFiles || [];

    // Always prefer server files if they exist
    if (serverFiles.length > 0) {
      return serverFiles;
    }

    // For user messages without server files, check if this is a pending message with files
    if (isUserMessage && message.id?.replace(/-user$/, '') !== buttonMessageId) {
      const pendingFiles = getPendingFilesForMessage(conversationId);

      if (pendingFiles.length > 0) {
        // Only show pending files on the most recent user message
        // This handles the first message case when files are uploaded but message not yet sent
        const lastUserMessageIndex = [...allMessages]
          .reverse()
          .findIndex(msg => msg.role === 'user');

        const actualLastUserMessageIndex = lastUserMessageIndex >= 0
          ? allMessages.length - 1 - lastUserMessageIndex
          : -1;

        if (messageIndex === actualLastUserMessageIndex) {
          return pendingFiles;
        }
      }
    }

    return serverFiles;
  }, [conversationId, getPendingFilesForMessage, buttonMessageId]);

  // Enhanced renderMessage function with access to conversation state
  const renderMessage = useCallback((
    message: Message,
    index: number,
    onAction: (action: MessageActionType, messageId: string) => void,
    allMessages: Message[],
    showCheckResult: boolean,
    onCheckResult: () => void,
    isCheckingResult: boolean
  ) => {
    // Convert simple message to ConversationMessage format for our components
    const sourceMetadata = ((message as any).metadata || {}) as Record<string, any>;
    const enhancedMessage: ConversationMessage = {
      id: message.id || `msg-${Date.now()}-${index}`,
      role: message.role,
      content: message.content,
      timestamp: message.timestamp || new Date(),

      // Include message-level file associations with optimistic display
      uploadedFiles: getMessageFiles(message, message.role === 'user', index, allMessages),
      upload_session_id: (message as any).upload_session_id,

      genie_space_names: (message as any).genie_space_names,
      genie_conversation_id: (message as any).genie_conversation_id,
      genie_message_id: (message as any).genie_message_id,
      toolCalls: (message as any).toolCalls || [],

      actions: {
        canEdit: message.role === 'user',
        canDelete: true,
        canRegenerate: message.role === 'assistant',
        canPlayAudio: message.role === 'assistant',
        isBookmarked: false,
        isLiked: false,
        isDisliked: false,
      },
      metadata: {
        ...sourceMetadata,
        // Add simple sample data for visual demo
        agentInfo: message.role === 'assistant' && sourceMetadata.agentInfo
        ? Array.isArray(sourceMetadata.agentInfo)
          ? sourceMetadata.agentInfo
          : [sourceMetadata.agentInfo]
        : undefined,
        // Citations are now extracted automatically by MessageMarkdown component
        originalMessageId: sourceMetadata.originalMessageId,
        hasFiles: getMessageFiles(message, message.role === 'user', index, allMessages).length > 0,
      },
    };

    // Ensure unique key by combining message ID with index
    const uniqueKey = `${enhancedMessage.id}-${index}`;

    return (
      <React.Fragment key={uniqueKey}>
        {/* Show message-level attachments above user messages */}
        {message.role === 'user' && enhancedMessage.uploadedFiles && enhancedMessage.uploadedFiles.length > 0 && (
          <MessageAttachmentList
            files={enhancedMessage.uploadedFiles}
            messageId={enhancedMessage.id}
            readOnly={true}
            showCount={false}
          />
        )}
        <MemoizedMessageComponent
          message={enhancedMessage}
          conversationId={conversationId}
          onAction={onAction}
          showCheckResult={showCheckResult}
          onCheckResult={onCheckResult}
          isCheckingResult={isCheckingResult}
        />
      </React.Fragment>
    );
  }, [getMessageFiles, conversationId]);

  // State for the edit confirmation modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<{
    messageId: string;
    content: string;
    hasMessagesBelow: boolean;
    messageIndex: number;
  } | null>(null);

  // Track previous conversation ID to detect transitions
  const prevConversationIdRef = useRef<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const performEdit = useCallback((messageId: string, newContent: string) => {
    // Set editing state when edit begins
    setEditingMessage(messageId);

    editMessage({
      messageId,
      newContent,
      onSuccess: (response) => {
        // Clear editing state when edit completes successfully
        clearEditingMessage();
        // Update thought process message ID to new message ID and sync conversation detail query
        setMessageID(response.message_id)
      },
      onError: (error) => {
        // Clear editing state when edit fails
        clearEditingMessage();
        console.error("Failed to edit message:", error);
      },
    });
  }, [editMessage, setEditingMessage, clearEditingMessage, setMessageID]);

  const handleAction = useCallback((action: MessageActionType, messageId: string, payload?: string) => {
    if (action == 'show_thought_process') {
      handleShowThoughtProcess(messageId);
    }
    if (action === 'edit' && payload) {
      // Get all displayed messages from the current state
      const currentMessages = messages ||
                             (query?.data ? transformConversationHistory(query.data) : []);

      // Find the index of the message being edited
      const messageIndex = currentMessages.findIndex(
        msg => msg.id === messageId ||
              (typeof msg.id === 'string' && typeof messageId === 'string' &&
               msg.id.replace(/-user$/, '') === messageId.replace(/-user$/, ''))
      );

      // Check if there are messages below the answer of this one
      const hasMessagesBelow = messageIndex !== -1 && messageIndex + 1 < currentMessages.length - 1;

      if (hasMessagesBelow) {
        // Store pending edit and show confirmation modal
        setPendingEdit({
          messageId,
          content: payload,
          hasMessagesBelow,
          messageIndex
        });
        setEditModalOpen(true);
      } else {
        // No messages below, proceed with edit directly
        performEdit(messageId, payload);
      }
    }
    else if (action === 'delete') {
      // TODO: delete message on delete action this is in phase 2
      console.log("Message deleted[sploof]:", messageId);
    }
  }, [messages, query?.data, performEdit, handleShowThoughtProcess]);


  const handleConfirmEdit = useCallback(() => {
    if (pendingEdit) {
      performEdit(pendingEdit.messageId, pendingEdit.content);
      setEditModalOpen(false);
      setPendingEdit(null);
    }
  }, [pendingEdit, performEdit]);

  const handleCancelEdit = useCallback(() => {
    setEditModalOpen(false);
    setPendingEdit(null);
  }, []);

  useEffect(() => {
    const hasConversationChanged = prevConversationIdRef.current &&
                                   prevConversationIdRef.current !== conversationId;

    if (hasConversationChanged) {
      setIsNavigating(true);
      // Use robust scroll for conversation switching
      scrollToBottomRobust('conversation-switch');
    }

    prevConversationIdRef.current = conversationId;
  }, [conversationId, scrollToBottomRobust]);

  // Reset navigation state when query finishes loading (no scroll trigger)
  useEffect(() => {
    if (!query?.isFetching && !query?.isPending) {
      setIsNavigating(false);
    }
  }, [query?.isFetching, query?.isPending]);

  // Reset navigation state when conversation data changes and scroll if navigating
  useEffect(() => {
    const hasMatchingData = query?.data && query.data.conversationID === conversationId;

    if (hasMatchingData) {
      setIsNavigating(false);

      // If we were navigating and now have data, ensure we scroll to bottom
      if (isNavigating) {
        scrollToBottomRobust('data-available-after-navigation');
      }
    }
  }, [query?.data, conversationId, isNavigating, scrollToBottomRobust]);

  // Transform API data to messages
  const apiMessages = useMemo(() => {
    if (query?.data) {
      return transformConversationHistory(query.data);
    }
    return [];
  }, [query?.data]);

  // Enhanced loading state logic following TanStack Query best practices
  // Show loading skeleton when appropriate for conversation navigation
  const isLoading = useMemo(() => {
    // Never show loading skeleton for home screen optimistic content
    if (isHomeScreen && messages && messages.length > 0) {
      return false;
    }

    // Check if we have matching cached data for the current conversation
    const hasMatchingCachedData = query?.data && query.data.conversationID === conversationId;

    // Show loading if:
    // 1. Explicitly loading (prop from parent)
    // 2. Query is pending (initial load)
    // 3. Query is fetching AND we don't have matching cached data
    // 4. We're navigating between conversations AND don't have matching data
    const shouldShowLoading = loading ||
                             query?.isPending ||
                             (query?.isFetching && !hasMatchingCachedData) ||
                             (isNavigating && !hasMatchingCachedData);

    return shouldShowLoading;
  }, [
    isHomeScreen,
    messages,
    loading,
    query?.isPending,
    query?.isFetching,
    query?.data,
    conversationId,
    isNavigating
  ]);

  // Detect if we're transitioning between conversations with cached data
  const isTransitioning = useMemo(() =>
    query?.isFetching && query?.data && isNavigating,
    [query?.isFetching, query?.data, isNavigating]
  );

  const errorMessage = useMemo(() =>
    error || query?.error?.message,
    [error, query?.error?.message]
  );

  // Use provided messages or API messages (memoized)
  const conversationMessages = useMemo(() =>
    messages || apiMessages || [],
    [messages, apiMessages]
  );

  // Track whether this conversation has ever had messages rendered.
  // Guards against a race condition where the upload_complete WS event triggers a
  // server refetch that returns before the new message is committed to the DB,
  // transiently overwriting the correct cache data and making messages appear empty.
  const hadMessagesRef = useRef<{ id: string; had: boolean }>({ id: '', had: false });
  if (hadMessagesRef.current.id !== conversationId) {
    hadMessagesRef.current = { id: conversationId, had: false };
  }
  if (conversationMessages.length > 0) {
    hadMessagesRef.current.had = true;
  }

  // Check if this is a development conversation
  const isDevConversation = conversationId && conversationId.startsWith('dev-');

  // Determine if this is a new conversation based on available data
  // Don't show empty state if we're loading (even with no messages)
  const isNewConversation = !conversationId ||
    conversationId.includes('fresh') ||
    conversationId.includes('new') ||
    (conversationMessages.length === 0 && !hadMessagesRef.current.had && !isDevConversation && !isLoading && !errorMessage && !query?.isFetching);

  // Create simple mock data for development conversation
  const getDevMockMessages = (conversationId: string): Message[] => {
    if (conversationId === 'dev-sample-conversation') {
      return [
        {
          id: 'dev-msg-1',
          role: 'user',
          content: 'What are our current AI governance policies and how do they align with industry standards?',
          timestamp: new Date(Date.now() - 10 * 60 * 1000),
        },
        {
          id: 'dev-msg-2',
          role: 'assistant',
          content: 'Based on our internal policies and industry best practices, here are the key areas of AI governance at Fonterra:\n\n## Current AI Governance Framework\n\n**1. Responsible AI Principles**\n- Transparency and explainability in AI decision-making\n- Fairness and bias mitigation across all AI applications\n- Privacy protection and data security\n- Human oversight and accountability\n\n**2. Compliance Standards**\n- Alignment with ISO/IEC 23053 framework for AI risk management\n- Adherence to GDPR and local data protection regulations\n- Integration with existing quality management systems\n\n**3. Risk Assessment Process**\n- Comprehensive AI impact assessments before deployment\n- Regular audits and monitoring of AI systems\n- Incident response procedures for AI-related issues\n\nWould you like me to elaborate on any specific aspect of our AI governance framework?',
          timestamp: new Date(Date.now() - 8 * 60 * 1000),
        },
        {
          id: 'dev-msg-3',
          role: 'user',
          content: 'Can you provide more details about our bias mitigation strategies?',
          timestamp: new Date(Date.now() - 5 * 60 * 1000),
        },
        {
          id: 'dev-msg-4',
          role: 'assistant',
          content: 'Absolutely! Our bias mitigation strategies are comprehensive and cover the entire AI lifecycle:\n\n## Pre-Development Phase\n- **Data Auditing**: Regular assessment of training data for representation gaps\n- **Stakeholder Consultation**: Involving diverse teams in AI system design\n- **Ethical Review**: Ethics committee approval for high-risk AI applications\n\n## Development Phase\n- **Diverse Development Teams**: Ensuring varied perspectives in AI development\n- **Bias Testing**: Systematic testing for algorithmic bias across different demographics\n- **Fairness Metrics**: Implementation of quantitative fairness measures\n\n## Post-Deployment Phase\n- **Continuous Monitoring**: Real-time monitoring of AI system outputs\n- **Feedback Loops**: Mechanisms for users to report bias incidents\n- **Regular Retraining**: Periodic model updates with bias-corrected data\n\n## Specific Tools and Techniques\n- Fairness-aware machine learning algorithms\n- Synthetic data generation for underrepresented groups\n- Adversarial testing for bias detection\n- Explainable AI techniques for transparency\n\nThese strategies help ensure our AI systems operate fairly across all user groups and business contexts.',
          timestamp: new Date(Date.now() - 2 * 60 * 1000),
        },
      ];
    }
    return [];
  };

  // Memoize dev mock messages to prevent recreating on every render
  const devMockMessages = useMemo(() =>
    isDevConversation ? getDevMockMessages(conversationId) : [],
    [isDevConversation, conversationId]
  );

  // Memoize display messages to prevent unnecessary re-renders
  const displayMessages = useMemo(() =>
    isNewConversation
      ? []
      : isDevConversation
        ? devMockMessages
        : conversationMessages,
    [isNewConversation, isDevConversation, devMockMessages, conversationMessages]
  );

  const selectedAgents = useChatStore(state => state.conversationInputs[conversationId]?.selectedAgents);
  const isSending = useIsMessageSending();
  const isStreaming = useStreamingStore(state => state.isStreaming);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const navigateToChat = useCallback(() => {
    navigate({ to: '/chat/$conversationId', params: { conversationId } });
  }, [navigate, conversationId]);
  const { sendMessage } = useChatActions(conversationId, navigateToChat);
  const [nowMs, setNowMs] = useState(Date.now);
  useEffect(() => {
    if (!FEATURES.showDigitalMaintenanceCheckResult || !check) return undefined;
    setNowMs(Date.now());
    let timer: number;
    const onDeadline = () => {
      const remaining = check.eligibleAtMs - Date.now();
      if (remaining > 0) timer = window.setTimeout(onDeadline, remaining);
      else setNowMs(Date.now());
    };
    if (check.eligibleAtMs > Date.now()) {
      timer = window.setTimeout(onDeadline, check.eligibleAtMs - Date.now());
    }
    return () => window.clearTimeout(timer);
  }, [conversationId, check?.eligibleAtMs]);

  const latest = displayMessages[displayMessages.length - 1];
  const latestId = latest?.id?.replace(/-assistant$/, '');
  const canCheckResult = FEATURES.showDigitalMaintenanceCheckResult
    && !isDevConversation
    && !isLoading
    && !errorMessage
    && canShowDigitalMaintenanceCheckResult({
      check,
      selectedAgents,
      latestMessage: latest,
      nowMs,
      isSending: isSending || isStreaming,
    });

  const handleCheckResult = useCallback(() => {
    const state = useChatStore.getState();
    const activeCheck = state.digitalMaintenanceChecks[conversationId];
    const lastTurn = queryClient.getQueryData<ConversationDetailResponse>(
      conversationQueryKeys.detail(conversationId)
    )?.conversationHistory?.at(-1);
    // Recheck eligibility at click time: the conversation, latest reply, or send state
    // may have changed since the button was rendered.
    if (!FEATURES.showDigitalMaintenanceCheckResult
      || !activeCheck
      || Date.now() < activeCheck.eligibleAtMs
      || !isDigitalMaintenanceOnly(state.getSelectedAgents(conversationId))
      || activeCheck.latestMessageId !== latestId
      || lastTurn?.message_id !== activeCheck.latestMessageId
      || !isDigitalMaintenancePendingResult(lastTurn.digital_maintenance_status)
      || !canCheckResult || isChatSendInFlight(conversationId)
      || queryClient.getMutationCache().getAll().some(
        mutation => mutation.state.status === 'pending' && mutation.options.meta?.type === 'chat'
      )
      || useStreamingStore.getState().isStreaming) return;
    void sendMessage(
      DIGITAL_MAINTENANCE_RESULT_COMMAND,
      { agents: state.getSelectedAgents(conversationId), model: state.getSelectedModel(conversationId) },
      'digitalMaintenanceCheck'
    ).catch((error: unknown) => {
      console.error('Failed to check Digital Maintenance result:', error);
      showNotification('Unable to check result. Please try again.');
    });
  }, [conversationId, queryClient, latestId, canCheckResult, sendMessage]);

  // Note: Conversation-level files removed - now using message-level files

  // Memoize rendered messages to prevent unnecessary re-renders - MUST be called before any early returns
  const renderedMessages = useMemo(() =>
    displayMessages.map((message: Message, index: number) =>
      renderMessage(
        message,
        index,
        handleAction,
        displayMessages,
        Boolean(canCheckResult && index === displayMessages.length - 1),
        handleCheckResult,
        isSending
      )
    ),
    [displayMessages, renderMessage, handleAction, canCheckResult, handleCheckResult, isSending]
  );

  // Auto-scroll when messages change (immediate scroll for continuous following)
  useEffect(() => {
    if (displayMessages.length > 0) {
      // Use instant scroll for message changes (no delays for streaming/new messages)
      scrollToBottomInstant();
    }
  }, [displayMessages, scrollToBottomInstant]);

  // Now all hooks have been called before any conditional returns

  // Conditional rendering based on state
  if (isLoading) {
    return <ConversationLoadingState isTransitioning={conversationMessages.length > 0} />;
  }

  // Show transition overlay when switching between conversations with existing data
  if (isTransitioning) {
    return (
      <div style={{ position: 'relative', height: '100%', flex: 1, minHeight: '100%' }}>
        {/* Show previous conversation content with overlay */}
        <div style={{ opacity: 0.3, height: '100%' }}>
          <Stack
            gap={CONVERSATION_LAYOUT.GAP}
            style={{
              flex: 1,
              padding: '36px 18px 24px 18px',
              minHeight: '100%',
              justifyContent: 'flex-start'
            }}
          >
            <Box maw={CONVERSATION_LAYOUT.MAX_WIDTH} w="100%" mx="auto">
              {renderedMessages}
            </Box>
          </Stack>
        </div>
        {/* Loading overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(2px)'
        }}>
          <ConversationLoadingState isTransitioning={true} />
        </div>
      </div>
    );
  }

  if (errorMessage) {
    const handleRetry = () => {
      if (query?.refetch) {
        query.refetch();
      }
    };
    return <ConversationErrorState error={errorMessage} onRetry={query?.refetch ? handleRetry : undefined} />;
  }

  if (displayMessages.length === 0) {
    // If this conversation previously had messages but they transiently disappeared
    // (race condition: stale refetch overwrote the cache), show a transitional loading
    // state rather than flashing the empty/sample-message screen.
    if (hadMessagesRef.current.had) {
      return <ConversationLoadingState isTransitioning={true} />;
    }
    return <ConversationEmptyState />;
  }

  return (
    <>
      <Stack
        gap={CONVERSATION_LAYOUT.GAP}
        style={{
          flex: 1,
          padding: '36px 18px 24px 18px',
          minHeight: '100%',
          justifyContent: 'flex-start'
        }}
      >
        <Box maw={CONVERSATION_LAYOUT.MAX_WIDTH} w="100%" mx="auto">
          {renderedMessages}
        </Box>
      </Stack>

      <Modal
        opened={editModalOpen}
        onClose={handleCancelEdit}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        <DynamicConfirmationModal
          title="Warning: Edit will delete messages"
          message={"This action will delete all responses below this message."}
          confirmLabel="Continue"
          cancelLabel="Cancel"
          iconType="warning"
          isProcessing={false}
          onConfirm={handleConfirmEdit}
          onCancel={handleCancelEdit}
        />
      </Modal>
    </>
  );
});

export type { ConversationContentProps, Message };
export { ConversationEmptyState, SampleMessageBubble };
